import { CameraAltOutlined } from "@mui/icons-material";
import { Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import { useEffect, useRef, useState } from "react";

interface CameraCaptureButtonProps {
  onCapture: (file: File) => void;
}

// Opens the device camera in a dialog and returns the captured live photo as a File
export function CameraCaptureButton(props: CameraCaptureButtonProps) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  function stopStream() {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError('');
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then(stream => {
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => setError('Unable to access camera. Please allow camera permission.'));
    return () => {
      cancelled = true;
      stopStream();
    };
  }, [open]);

  function capture() {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    canvas.toBlob(blob => {
      if (!blob) return;
      props.onCapture(new File([blob], `photo-${Date.now()}.png`, { type: 'image/png' }));
      setOpen(false);
    }, 'image/png');
  }

  return (
    <>
      <span className='gbk-icon-span large' style={{ cursor: 'pointer' }} onClick={() => setOpen(true)}>
        <CameraAltOutlined color='primary' /> Take Photo
      </span>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth='sm' fullWidth>
        <DialogContent>
          {error ? error : <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%' }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={capture} disabled={!!error}>Capture</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
